import React from 'react';
import { useTranslation } from 'react-i18next';

import { LicenseModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { BuyDesignLicenseParameters } from '../../../../../typings/api/skymap/rest/v0/user';
import { RequestParamsToApiPaths } from '../../../utils/typescript-utils';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { DialogContent } from './dialog-content';
import { useBuyQuery } from './use-buy-query';

type Props = {
  /**
   * license.company and license.company.country is expected to be defined.
   * TODO: Consider redefine these to required with WithRequired (type-jest) or similar.
   */
  license: LicenseModel;
  projectId: string;
  onClose: () => void;
  onFormSubmitted: () => void;
};

export type ApiRequestPath = RequestParamsToApiPaths<BuyDesignLicenseParameters>;

const BuyDesignLicenseDialog = (props: Props) => {
  const { t } = useTranslation();
  const { handleError, buildErrorList, clearErrors, getErrorForPath, hasApiError } =
    useErrorHandling<ApiRequestPath>();
  const [formDisabled, setFormDisabled] = React.useState(false);
  const buyQuery = useBuyQuery({
    companyId: props.license.company!.id,
    onError: (error) => handleError(error),
    onMutate: clearErrors,
    onSuccess: props.onFormSubmitted,
  });

  if (
    !formDisabled &&
    hasApiError((x) => x.originalError?.code === 'license:subscription_is_already_active')
  ) {
    setFormDisabled(true);
  }

  return (
    <Dialog width={600} onClose={props.onClose}>
      {{
        header: t('buyDesignLicenseDialog.title', { ns: 'components' }),
        content: (
          <DialogContent
            buildErrorList={buildErrorList}
            buyQuery={buyQuery}
            company={props.license.company!}
            formDisabled={formDisabled}
            getErrorForPath={getErrorForPath}
          />
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={formDisabled || buyQuery.isPending}
                form="designLicenseForm"
                loading={buyQuery.isPending}
                type="submit"
                variant="contained"
              >
                {t('buyDesignLicenseDialog.buyButton', { ns: 'components' })}
              </Button>

              <Button variant="text" onClick={props.onClose}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { BuyDesignLicenseDialog };
